import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Popover,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Tooltip,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiUpload,
} from 'react-icons/fi';
import { getSortIcon } from '../utils/sortIcon';
import { contactsAPI } from '../services/api';
import { useSelector } from 'react-redux';
import { selectGlobalTags } from '../store/slices/tagsSlice';
import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import TableLoader from '../components/TableLoader';
import { useMultiSelect } from '../hooks/useMultiSelect';
import BulkActionToolbar from '../components/BulkActionToolbar';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { showSuccess, showError } from '../utils/toast';
import { colors } from '../theme/modernTheme';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';

export default function Contacts() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const canCreate = user?.role === 'ADMIN' || user?.role === 'STAFF';
  const canEdit = user?.role === 'ADMIN' || user?.role === 'STAFF';
  const canDelete = user?.role === 'ADMIN'; // Only ADMIN can delete contacts
  const [contacts, setContacts] = useState([]);

  // Filter states - all filters require Apply button or Enter key
  const [searchInput, setSearchInput] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [tagInput, setTagInput] = useState([]);
  const [createdByInput, setCreatedByInput] = useState('');

  // Applied filter states (used for API calls)
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState('');
  const tags = useSelector(selectGlobalTags);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 50; // Fixed to 50 rows per page
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('desc');
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState(null);
  const [selectedContactTags, setSelectedContactTags] = useState([]);

  // Multi-select state
  const multiSelect = useMultiSelect(contacts, totalCount, 'id');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Load contacts on mount and when dependencies change
  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, tagFilter, createdByFilter, page, rowsPerPage, sortBy, sortOrder]);

  // Load users for created_by filter
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await contactsAPI.getUsers();
      // Backend returns response.data.users
      const usersData = response.data?.users || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const params = {
        search: search,
        page: page + 1,
        limit: rowsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (typeFilter) params.type = typeFilter;
      if (tagFilter && tagFilter.length > 0) {
        // If multiple tags selected, send comma-separated IDs using tag_ids parameter
        params.tag_ids = tagFilter.map((tag) => tag.id).join(',');
      }
      if (createdByFilter) params.created_by = createdByFilter;

      const response = await contactsAPI.getAll(params);
      setContacts(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate('/contacts/new');
  };

  const handleView = async (contact) => {
    window.open(`/contacts/${contact.id}`, '_blank');
  };

  const handleEdit = (contact) => {
    window.open(`/contacts/${contact.id}?edit=true`, '_blank');
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      showLoading();
      await contactsAPI.delete(contactToDelete.id);
      multiSelect.removeFromSelection(contactToDelete.id);
      setDeleteDialogOpen(false);
      setContactToDelete(null);

      // Adjust page if needed after deletion
      const newTotalCount = totalCount - 1;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadContacts();
    } catch (error) {
      showError('Failed to delete contact');
    } finally {
      hideLoading();
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  const handleApplyFilters = () => {
    // Apply all filters: search, type, tags, and created_by
    setSearch(searchInput);
    setTypeFilter(typeInput);
    setTagFilter(tagInput);
    setCreatedByFilter(createdByInput);
    setPage(0); // Reset to first page when applying filters
    multiSelect.clearSelection(); // Clear selection when filters change
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setTypeInput('');
    setTagInput([]);
    setCreatedByInput('');
    setSearch('');
    setTypeFilter('');
    setTagFilter([]);
    setCreatedByFilter('');
    setPage(0); // Reset to first page when clearing filters
    multiSelect.clearSelection(); // Clear selection when filters cleared
  };

  const handleFilterKeyPress = (e) => {
    // Apply filters when Enter key is pressed
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasActiveFilters = searchInput || typeInput || tagInput.length > 0 || createdByInput;

  // Check if any filter has changed from applied state
  const hasFilterChanges =
    searchInput !== search ||
    typeInput !== typeFilter ||
    JSON.stringify(tagInput) !== JSON.stringify(tagFilter) ||
    createdByInput !== createdByFilter;

  const handleBulkDelete = async () => {
    try {
      showLoading();
      const selectionData = multiSelect.getSelectionData();
      const deleteCount = multiSelect.selectedCount;

      // Build filters object from current filter state
      const filters = {};
      if (search) filters.search = search;
      if (typeFilter) filters.type = typeFilter;
      if (tagFilter && tagFilter.length > 0) {
        filters.tag_ids = tagFilter.map((tag) => tag.id).join(',');
      }

      await contactsAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();

      // Adjust page if needed after bulk deletion
      const newTotalCount = totalCount - deleteCount;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadContacts();
      showSuccess(`Successfully deleted ${deleteCount} contact(s)`);
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete contacts');
    } finally {
      hideLoading();
    }
  };

  // Contacts are now sorted server-side, no need for client-side sorting
  const sortedContacts = contacts;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Contacts
        </Typography>
        {canCreate && (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<FiUpload />}
              onClick={() => navigate('/contacts/import')}
              sx={{
                backgroundColor: colors.primary.main,
                '&:hover': { backgroundColor: colors.primary.dark },
                borderRadius: '4px',
              }}
            >
              Upload CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<FiPlus />}
              onClick={handleAdd}
              sx={{
                backgroundColor: colors.primary.main,
                '&:hover': { backgroundColor: colors.primary.dark },
                borderRadius: '4px',
              }}
            >
              Add Contact
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          boxShadow: 'none',
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search contacts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setPage(0);
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background.paper,
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={typeInput}
              onChange={(e) => setTypeInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                All Types
              </MenuItem>
              <MenuItem
                value="INDIVIDUAL"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Individual
              </MenuItem>
              <MenuItem
                value="ORGANIZATION"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Organization
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.8}>
            <Autocomplete
              multiple
              size="small"
              options={tags}
              value={tagInput}
              onChange={(event, newValue) => {
                setTagInput(newValue);
                // Auto-apply when clearing all tags
                if (newValue.length === 0) {
                  setTimeout(() => {
                    setTagFilter([]);
                    setPage(0);
                  }, 0);
                }
              }}
              disabled={multiSelect.selectedCount > 0}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder={tagInput.length === 0 ? 'Search tags...' : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {value.slice(0, 1).map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        backgroundColor: tag.color || '#8B5FBF',
                        color: '#fff',
                        borderRadius: '4px',
                        height: 20,
                        maxWidth: '120px',
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                  {value.length > 1 && (
                    <Chip
                      label={`+${value.length - 1}`}
                      size="small"
                      sx={{
                        color: '#4086f6ff',
                        borderRadius: '4px',
                        height: 24,
                        minWidth: '36px',
                        fontWeight: 800,
                        fontSize: '0.8rem',
                      }}
                    />
                  )}
                </Box>
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: '2px 8px', minHeight: '32px' }}>
                  <Checkbox
                    size="medium"
                    checked={selected}
                    sx={{
                      p: 0.5,
                      '& .MuiSvgIcon-root': {
                        fontSize: 28,
                      },
                      transform: 'scale(1.2)',
                    }}
                  />
                  <Chip
                    label={option.name}
                    size="small"
                    sx={{
                      backgroundColor: option.color || '#8B5FBF',
                      color: '#fff',
                      borderRadius: '4px',
                    }}
                  />
                </li>
              )}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                      borderRadius: 0,
                      backgroundColor: colors.action.hover,
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      '&:hover': {
                        borderRadius: 0,
                        backgroundColor: colors.action.hover,
                      },
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Created By"
              value={createdByInput}
              onChange={(e) => setCreatedByInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                All Users
              </MenuItem>
              {Array.isArray(users) &&
                users.map((user) => (
                  <MenuItem
                    key={user.id}
                    value={user.id}
                    sx={{
                      p: '2px 8px',
                      minHeight: '32px',
                      '&:hover': { borderRadius: 0 },
                      '&.Mui-selected': { borderRadius: 0 },
                    }}
                  >
                    {user.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                disableRipple
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  mr: -1,
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={1.6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 || multiSelect.selectedCount > 0
                }
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="contacts"
      />

      {loading ? (
        <TableLoader message="Loading contacts..." />
      ) : sortedContacts.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No contacts found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '2%', py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      // Only select contacts that can be deleted
                      const deletableContacts = contacts.filter((c) => c.can_delete !== false);
                      if (multiSelect.isAllCurrentPageSelected) {
                        multiSelect.clearSelection();
                      } else {
                        deletableContacts.forEach((contact) => {
                          if (!multiSelect.isSelected(contact)) {
                            multiSelect.toggleSelect(contact);
                          }
                        });
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      padding: '4px',
                      color: colors.text.secondary,
                      '& .MuiSvgIcon-root': { fontSize: 28 },
                      transform: 'scale(1.2)',
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: 0,
                    width: '160px',
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                  }}
                  onClick={() => handleSort('name')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Name
                    {getSortIcon(sortBy, sortOrder, 'name')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '180px',
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                  }}
                  onClick={() => handleSort('primary_email')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Email
                    {getSortIcon(sortBy, sortOrder, 'primary_email')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    width: '130px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Tags
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    width: '20px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {multiSelect.selectedCount > 0 ? '' : 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContacts.map((contact, index) => (
                <TableRow
                  key={contact.id}
                  onClick={() => handleView(contact)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: multiSelect.isSelected(contact)
                      ? colors.primary.main + '10'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: multiSelect.isSelected(contact)
                        ? colors.primary.main + '20'
                        : colors.action.row,
                    },
                    '&:not(:last-of-type)': {
                      borderBottom: `1px solid ${colors.border.main}`,
                    },
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    sx={{ py: 1.5, px: 1, width: '20px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      title={
                        contact.can_delete === false
                          ? 'Cannot delete: Contact has enrolled in  campaigns or has donations'
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <span
                        style={{
                          cursor: contact.can_delete === false ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Checkbox
                          size="medium"
                          checked={multiSelect.isSelected(contact)}
                          onChange={(e) => {
                            e.stopPropagation();
                            multiSelect.toggleSelect(contact);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          disabled={contact.can_delete === false}
                          sx={{
                            padding: '4px',
                            '& .MuiSvgIcon-root': { fontSize: 28 },
                            transform: 'scale(1.2)',
                            color: colors.text.secondary,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '&.Mui-disabled': {
                              color: colors.text.disabled,
                              cursor: 'not-allowed',
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingLeft: 0,
                      maxWidth: '160px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Tooltip
                        title={
                          contact.type === 'INDIVIDUAL'
                            ? `${contact.first_name} ${contact.last_name || ''}`
                            : contact.organization_name
                        }
                        arrow
                        placement="top"
                      >
                        <span>
                          {contact.type === 'INDIVIDUAL'
                            ? `${contact.first_name} ${contact.last_name || ''}`
                            : contact.organization_name}
                        </span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '180px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Tooltip title={contact.primary_email || '--'} arrow placement="top">
                        <span>{contact.primary_email || '--'}</span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '100px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {contact.phone_1
                      ? `+${contact.phone_1_country_code || ''}${contact.phone_1}`.trim() || '--'
                      : '--'}
                  </TableCell>

                  <TableCell sx={{ py: 1, maxWidth: '120px' }}>
                    <Box display="flex" gap={0.5} alignItems="center">
                      {(() => {
                        // Parse tags - backend returns comma-separated string
                        const tagNames = contact.tags
                          ? contact.tags.split(',').filter((t) => t.trim())
                          : [];

                        if (tagNames.length === 0) {
                          return (
                            <Typography variant="caption" color="text.secondary">
                              No tags
                            </Typography>
                          );
                        }

                        // Show only first tag - match with actual tag object to get color
                        const firstTagName = tagNames[0].trim();
                        const firstTagObj = tags.find((t) => t.name === firstTagName);
                        const backgroundColor = firstTagObj?.color || '#5A6ACF';
                        const textColor = colors.background.paper;
                        return (
                          <>
                            <Chip
                              label={firstTagName}
                              size="small"
                              sx={{
                                backgroundColor: backgroundColor,
                                color: textColor,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                borderRadius: '4px',
                              }}
                            />
                            {tagNames.length > 1 && (
                              <Chip
                                label={`+${tagNames.length - 1}`}
                                size="large"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTagPopoverAnchor(e.currentTarget);

                                  const remainingTags = tagNames.slice(1).map((tagName) => {
                                    const tagObj = tags.find((t) => t.name === tagName.trim());
                                    return (
                                      tagObj || {
                                        name: tagName.trim(),
                                        color: null,
                                      }
                                    );
                                  });

                                  setSelectedContactTags(remainingTags);
                                }}
                                sx={{
                                  color: colors.primary.main,
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  borderRadius: '4px',
                                  backgroundColor: 'transparent',
                                  p: 0,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'rgba(55, 61, 251, 0.08)',
                                  },
                                }}
                              />
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '120px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Tooltip title={contact.created_by_name || '--'} arrow placement="top">
                        <span>{contact.created_by_name || '--'}</span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  {/* <TableCell align="center" sx={{ width: "90px", py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.primary }}>
                      {contact.campaign_count || 0}
                    </Typography>
                  </TableCell> */}
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1 }}>
                    {multiSelect.selectedCount > 0 ? (
                      <Box sx={{ width: '72px' }} />
                    ) : (
                      <Box sx={{ display: 'flex', gap: '4px', minWidth: '72px' }}>
                        {canEdit ? (
                          <IconButton
                            size="small"
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: colors.primary.main,
                              },
                            }}
                            onClick={() => handleEdit(contact)}
                            title="Edit"
                          >
                            <FiEdit2 size={16} />
                          </IconButton>
                        ) : (
                          <Box sx={{ width: '36px' }} />
                        )}

                        {canDelete && (
                          <Tooltip
                            title={
                              contact.can_delete === false
                                ? 'Cannot delete: Contact has enrolled in  campaigns or has donations'
                                : 'Delete'
                            }
                            arrow
                            placement="top"
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={contact.can_delete === false}
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: colors.error.main,
                                  },
                                  '&.Mui-disabled': {
                                    color: colors.text.disabled,
                                    cursor: 'not-allowed',
                                  },
                                }}
                                onClick={() => handleDeleteClick(contact)}
                              >
                                <FiTrash2 size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && sortedContacts.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
            opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
            pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0 || multiSelect.selectedCount > 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={
              page >= Math.ceil(totalCount / rowsPerPage) - 1 || multiSelect.selectedCount > 0
            }
            sx={{
              marginLeft: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Tags Popover */}
      <Popover
        open={Boolean(tagPopoverAnchor)}
        anchorEl={tagPopoverAnchor}
        onClose={() => setTagPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            mt: 0.5,
            maxWidth: 300,
          },
        }}
      >
        <Box sx={{ p: 2, borderRadius: '4px' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: '#000000',
              fontSize: '0.9rem',
            }}
          >
            Additional Tags
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              borderRadius: '4px',
            }}
          >
            {selectedContactTags.map((tag, index) => {
              const backgroundColor = tag.color || 'rgba(139, 95, 191, 0.1)';
              const textColor = tag.color ? '#fff' : '#8B5FBF';
              return (
                <Chip
                  key={index}
                  label={tag.name}
                  size="small"
                  sx={{
                    backgroundColor: backgroundColor,
                    color: textColor,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Popover>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="contact"
        deleting={loading}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="contacts"
      />
    </Box>
  );
}
